import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { SearchFormResults } from './SearchFormResults';
import { SVG, Icons } from '../../../components/atoms/SVG';
import bem from '../../../lib/bemUtils/bem';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { CollapsibleWysiwygView } from '../../../_lib/components/Wysiwyg';
import { FetchType } from '../../../types/api/FetchType';
import { FACETS } from '../../../components/molecules/ResultListSettings/ResultListSettings.enums';
import ResultListSettings from '../../../components/molecules/ResultListSettings/ResultListSettings';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { SectionTypes } from '../../../types/api/Sections';
import { SearchResults } from '../../../types/Search.types';
import { SearchFormResetButton } from './SearchFormResetButton';
import { SearchFormSwitchButtons } from './SearchFormSwitchButtons';
import { useSonTranslation } from '../../../translation/useSonTranslation';

type SearchFormProps = {
  searchResult: SearchResults;
  type: 'content' | 'user';
  showSwitchButtons?: boolean;
  wantedFacets: FACETS[];
  modifier?: Modifier<'page-list'>;
};

export function SearchForm({
  searchResult,
  type,
  showSwitchButtons,
  wantedFacets,
  modifier,
}: SearchFormProps) {
  const { t } = useSonTranslation('SearchForm');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [focus, setFocus] = useState(false);
  const [hasInputValue, setHasInputValue] = useState(!!searchResult.term);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isInPageList = modifier === 'page-list';

  const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setHasInputValue(isNotNullOrUndefined(value) && value !== '');
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    searchParams.delete('page');

    if (inputRef.current?.value) {
      searchParams.set('q', inputRef.current.value);
    } else {
      searchParams.delete('q');
    }

    navigate(`?${searchParams.toString()}`, {
      state: {
        refetch: 'force',
        omitSection: [
          SectionTypes.personal,
          SectionTypes.navigation,
          SectionTypes.breadcrumb,
          SectionTypes.footer,
        ],
      },
    });
  };

  const resetInput = (event: React.FormEvent) => {
    event.preventDefault();
    if (inputRef.current) {
      inputRef.current.value = '';
      setHasInputValue(false);
      inputRef.current.focus();
    }
  };

  const fqPathId = searchResult.sectionFilter?.sectionParams?.fq_pathId;

  return (
    <div className={bem('search-form', modifier)}>
      <form
        // needs to be reactivated if action url should point to another endpoint
        action={searchResult.searchForm.url}
        onSubmit={onSubmit}
        role="search"
        className="search-form__form"
        ref={formRef}
        method="GET"
      >
        {fqPathId && <input type="hidden" name="fq_pathId" value={fqPathId} />}
        <div className="search-form__inner-container">
          <div className="search-form__padded-container">
            <p className="search-form__form-container">
              <span className="search-form__input-container">
                <input
                  ref={inputRef}
                  type="search"
                  className="search-form__input font-regular-13-13"
                  id={`${type}-search`}
                  name="q"
                  placeholder={t('search.title')}
                  required={!isInPageList}
                  title={t('search.title')}
                  aria-label={t('search.label')}
                  minLength={isInPageList ? undefined : 2}
                  maxLength={searchResult.maxLength}
                  autoComplete="off"
                  spellCheck="false"
                  aria-describedby="search-results-text"
                  defaultValue={searchResult.term || ''}
                  onChange={onSearchInputChange}
                  onFocus={() => setFocus(true)}
                  onBlur={() => setFocus(false)}
                />
                <SearchFormResetButton
                  onClick={resetInput}
                  focus={focus}
                  hasInputValue={hasInputValue}
                />
              </span>
              <button type="submit" className="search-form__button font-medium-15-15">
                <span className="search-form__text-container">{t('search.search')}</span>
                <span className="search-form__icon-container">
                  <SVG svgSpriteId={Icons.SEARCH_MAGNIFIER} svgClass="search-form__icon-search" />
                </span>
              </button>
            </p>
            {searchResult.term && <SearchFormResults searchResult={searchResult} pageList={isInPageList} />}
            {searchResult.hasUnsupportedSonSearchFeatures && (
              <p className="search-form__warning font-regular-15-21">{t('search.unsupportedSonSearchFeatures')}</p>
            )}
          </div>
        </div>
        {showSwitchButtons && (
          <SearchFormSwitchButtons
            searchLinks={searchResult.searchLinks || {}}
            currentType={type}
          />
        )}
        {searchResult.richtext && (
          <div className="search-form__help-text">
            <CollapsibleWysiwygView
              fetchType={FetchType.instant}
              href={searchResult.searchForm.url}
              embedded={searchResult.richtext}
            />
          </div>
        )}
        {searchResult.facets
          ? <ResultListSettings
              searchStrictness={searchResult.searchStrictness}
              facets={searchResult.facets}
              sort={searchResult.sort}
              filter={searchResult.filter}
              resetAllFiltersUrl={searchResult.resetAllFiltersUrl}
              wantedFacets={wantedFacets}
            />
          : null}
      </form>
    </div>
  );
}
